import { GridOption } from '@zoryn/components/grid'
import { ZMessage } from '@zoryn/components/message'
import type { HttpRequest } from '@zoryn/network'
import { buildDataSourcePath } from './constants'
import {
  batchValidateTableConfigs,
  fetchFieldConfigList,
  parseSqlFields,
} from './data-source-api'
import type { DataSourceValidateStatus, SqlDataSourceField } from './types'

export function sqlPreview(sql: unknown) {
  const text = String(sql ?? '')
    .replace(/\s+/g, ' ')
    .trim()
  return text.length > 80 ? `${text.slice(0, 80)}…` : text
}

export function statusLabel(status: DataSourceValidateStatus | undefined) {
  const map: Record<DataSourceValidateStatus, string> = {
    idle: '未校验',
    success: '通过',
    error: '失败',
    validating: '校验中',
  }
  return map[status ?? 'idle']
}

export function resolveValidateStatus(
  row: Record<string, unknown>
): DataSourceValidateStatus {
  const status = row._validateStatus as DataSourceValidateStatus | undefined
  if (status) return status
  return row.validateTime ? 'success' : 'idle'
}

async function enrichDataSourceRows(
  request: HttpRequest,
  rows: Record<string, unknown>[]
) {
  await Promise.all(
    rows.map(async (row) => {
      const system = String(row.systemFlag ?? '')
      const flag = String(row.dataSourceFlag ?? '')
      row._path = buildDataSourcePath(system, flag)
      row._validateStatus = row.validateTime ? 'success' : 'idle'

      if (row.id != null && row.id !== '') {
        try {
          const fields = await fetchFieldConfigList(request, Number(row.id))
          row._fields = fields
          row._fieldCount = fields.filter((item) => item.enabled).length
        } catch {
          row._fields = [] as SqlDataSourceField[]
          row._fieldCount = 0
        }
      } else {
        row._fields = [] as SqlDataSourceField[]
        row._fieldCount = 0
      }
    })
  )
}

export interface DataSourceGridHandlers {
  onCreate: () => void
  onEdit: (row: Record<string, unknown>) => void | Promise<void>
  onCurrentRowChange: (row: Record<string, unknown> | null) => void
  onAfterQuery: (rows: Record<string, unknown>[]) => void
}

export function createDataSourceGridOptions(
  request: HttpRequest,
  handlers: DataSourceGridHandlers
) {
  const options = new GridOption({
    module: '/system/table-config',
    toolbar: true,
    editable: false,
    delBtn: true,
    autoQuery: true,
    async afterQueryPage(rows) {
      await enrichDataSourceRows(request, rows)
      handlers.onAfterQuery(rows)
    },
    onCurrentRowChange(row) {
      handlers.onCurrentRowChange(row)
    },
    btns: [
      {
        label: '创建数据源',
        type: 'primary',
        onClick: () => handlers.onCreate(),
      },
      {
        label: '编辑',
        onClick: () => {
          const row = options.getCurrentRow()
          if (!row) {
            ZMessage.warning('请先选中要编辑的数据源')
            return
          }
          void handlers.onEdit(row)
        },
      },
      {
        label: '批量校验',
        onClick: () => void handleBatchValidate(request, options),
      },
    ],
    columns: [
      { type: 'checkbox' },
      {
        field: '_path',
        title: '访问路径',
        width: 120,
        formatter: ({ cellValue }) => String(cellValue ?? ''),
      },
      {
        field: 'systemFlag',
        title: '系统',
        width: 88,
      },
      {
        field: 'dataSourceFlag',
        title: '数据源标识',
        width: 120,
      },
      {
        field: 'dataSourceName',
        title: '数据源名称',
        width: 140,
      },
      {
        field: 'customSql',
        title: 'SQL',
        minWidth: 240,
        formatter: ({ cellValue }) => sqlPreview(cellValue),
      },
      {
        field: '_fieldCount',
        title: '字段',
        width: 72,
        align: 'center',
        formatter: ({ cellValue }) => String(cellValue ?? 0),
      },
      {
        field: '_validateStatus',
        title: '状态',
        width: 100,
        formatter: ({ row }) => statusLabel(resolveValidateStatus(row)),
      },
    ],
  })

  return options
}

async function handleBatchValidate(request: HttpRequest, options: GridOption) {
  const selected = options.getAllCheckbox()
  if (!selected.length) {
    ZMessage.warning('请先勾选要校验的数据源')
    return
  }

  const ids = selected
    .map((row) => Number(row.id))
    .filter((id) => Number.isFinite(id))
  if (!ids.length) return

  for (const row of selected) {
    row._validateStatus = 'validating'
  }

  try {
    const results = await batchValidateTableConfigs(request, ids)
    for (const item of results) {
      const row = selected.find((entry) => String(entry.id) === item.id)
      if (!row) continue
      row._validateStatus = item.result.ok ? 'success' : 'error'
      row.validateMessage = item.result.message
      row.validateTime = item.result.ok
        ? new Date().toISOString()
        : row.validateTime
      if (item.result.ok && row.customSql) {
        try {
          const fields = await parseSqlFields(request, String(row.customSql))
          row._fields = fields
          row._fieldCount = fields.filter((field) => field.enabled).length
        } catch {
          // keep existing fields
        }
      }
    }
    const failed = results.filter((item) => !item.result.ok).length
    if (failed) {
      ZMessage.warning(
        `批量校验完成：${results.length - failed} 通过，${failed} 失败`
      )
    } else {
      ZMessage.success(`批量校验完成：${results.length} 条全部通过`)
    }
  } catch (err) {
    ZMessage.error(err instanceof Error ? err.message : '批量校验失败')
  } finally {
    await options.queryByExamplePage?.()
  }
}
